/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import React, {
  MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import Divider from '@material-ui/core/Divider';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import {makeStyles} from '@material-ui/core/styles';
import {BulkActionType, CTCEntity} from '@ion/core/src/types/entities';
import {useEditorStyles} from '@ion/dls/src/hooks/useEditorStyles';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {BodyTypography} from '@ion/dls/src/typography/BodyTypography';
import {checkSCIDevice} from '../pages/devices/SCISettingsUtils';
import {setIsConflictsChecked} from './store/store';
import {BulkActions} from './types';

export interface ActionListProps {
  actions: BulkActions;
  onActionChange: (newAction: BulkActionType) => void;
  selectedRowEntities: CTCEntity[];
  areTotalRowsSelected: boolean;
  totalRowsCount?: number;
  selectedIdentifierType?: string;
  hasCreateJob?: boolean;
  hasUpdateAsset?: boolean;
  hasCreateDevice?: boolean;
  disableServiceEnablement?: boolean;
  hasVBusAccess?: boolean;
  hasFilePermissions?: boolean;
  hasVehicleProfile?: boolean;
  disableScheduleDeviceSync?: boolean;
  entityIdentifiers?: Array<[]>;
  hasDevicePassword?: boolean;
}

const useStyles = makeStyles(() => ({
  leftContainer : {
    flex            : 1,
    backgroundColor : dlsColor('white'),
    overflowY       : 'scroll'
  },
  highlightActionMenuItem : {
    backgroundColor : '#DCF6FC !important'
  },
  menuItem : {
    maxHeight   : '42px !important',
    boxSizing   : 'border-box',
    paddingLeft : '16px'
  }
}));

/**
 * This step will display the page containing the action list.
 */
export const ActionList = ({
  actions,
  onActionChange,
  selectedRowEntities,
  selectedIdentifierType,
  totalRowsCount,
  hasVBusAccess = false,
  hasCreateJob = false,
  hasUpdateAsset = false,
  hasCreateDevice = false,
  areTotalRowsSelected = false,
  disableScheduleDeviceSync = false,
  disableServiceEnablement = false,
  hasFilePermissions = false,
  hasVehicleProfile = false,
  hasDevicePassword = false,
  entityIdentifiers
}: ActionListProps) => {
  const {classes}                     = useEditorStyles();
  const localClasses                  = useStyles();
  const [actionValue, setActionValue] = useState<number>(0);
  const [
    vehicleProfileDisabled,
    setVehicleProfileDisabled
  ] = useState<boolean>(false);

  const {
    SERVICE_ENABLEMENT,
    SCI_UPDATE,
    SCHEDULE_DATA_SYNC,
    BULK_ADD,
    VBUS,
    JOB_CREATION,
    UNPAIR,
    VEHICLE_PROFILE,
    DEVICE_PASSWORD
  } = BulkActionType;

  const sciDisabled = useMemo(() => {
    if (!areTotalRowsSelected && selectedRowEntities?.length) {
      return !selectedRowEntities.some(checkSCIDevice);
    }
    return false;
  }, [selectedRowEntities, areTotalRowsSelected]);

  const MAX_ROW_COUNT = 10000;

  useEffect(() => {
    const totalSelectedDevices = areTotalRowsSelected ? totalRowsCount : entityIdentifiers?.length;
    if (!hasVehicleProfile || totalSelectedDevices > MAX_ROW_COUNT) {
      setVehicleProfileDisabled(true);
    }
  }, [hasVehicleProfile, totalRowsCount, areTotalRowsSelected]);

  const getActionStatus = (actionType: string): boolean =>
    (actionType === SERVICE_ENABLEMENT && disableServiceEnablement) ||
    (actionType === SCHEDULE_DATA_SYNC && disableScheduleDeviceSync) ||
    (actionType === SCI_UPDATE && sciDisabled) ||
    (actionType === BULK_ADD && (selectedIdentifierType === 'id' || !hasCreateDevice)) ||
    (actionType === VBUS && !hasVBusAccess) ||
    (actionType === JOB_CREATION && (!hasCreateJob || hasFilePermissions)) ||
    (actionType === UNPAIR && !hasUpdateAsset) ||
    (actionType === VEHICLE_PROFILE && vehicleProfileDisabled) ||
    (actionType === DEVICE_PASSWORD && !hasDevicePassword);

  const onActionClick = useCallback((newAction: BulkActionType): MouseEventHandler => {
    return (_e) => {
      onActionChange?.(newAction?.type);
      getActionMenuProperties(_e?.target?.innerText);
      // Reset conflicts checked state to reset the process label in BulkActionsDialog
      setIsConflictsChecked(false);
    };
  }, []);

  const getActionMenuProperties = useCallback((action: string) => {
    const actionmenuItem = actions?.findIndex((item) => item?.title === action);
    setActionValue(actionmenuItem);
  }, [actionValue]);

  return (
    <div className={localClasses.leftContainer}>
      <MenuList>
        {actions?.map((action, index) => (
          <div key={`actionsList_${index}`}>
            <MenuItem classes={{
              root     : localClasses.menuItem,
              selected : localClasses.highlightActionMenuItem
            }}
            selected={actionValue === index}
            disabled={getActionStatus(action.type)}
            onClick={onActionClick(action)}>
              <BodyTypography variant={'body2'} className={classes.body2}>
                {action.title}
              </BodyTypography>
            </MenuItem>
            <Divider />
          </div>
        ))}
      </MenuList>
    </div>
  );
};

export default ActionList;
